import dayjs from 'dayjs';
import React, { useCallback, useEffect, useState } from 'react';
import { Table, Button, Modal, Form, Input, Space, Tag, Select, message, Spin, Tabs, Drawer } from 'antd';
import { SendOutlined, DownloadOutlined } from '@ant-design/icons';
import DOMPurify from 'dompurify';
import apiClient from '../../services/apiClient';

interface Subscriber {
  id: string;
  email: string;
  name: string | null;
  status: 'active' | 'unsubscribed' | 'bounced';
  subscribed_at: string;
  unsubscribed_at: string | null;
}

interface NewsletterStats {
  activeCount: number;
  unsubscribedCount: number;
  bouncedCount: number;
  totalCount: number;
}

const NewsletterManagement: React.FC = () => {
  const [subscribers, setSubscribers] = useState<Subscriber[]>([]);
  const [stats, setStats] = useState<NewsletterStats>({
    activeCount: 0,
    unsubscribedCount: 0,
    bouncedCount: 0,
    totalCount: 0,
  });
  const [loading, setLoading] = useState(false);
  const [sendLoading, setSendLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState<'active' | 'unsubscribed' | 'bounced' | undefined>();
  const [searchTerm, setSearchTerm] = useState('');
  const [sendNewsletterModal, setSendNewsletterModal] = useState(false);
  const [sendForm] = Form.useForm();
  const [previewDrawer, setPreviewDrawer] = useState(false);
  const [previewContent, setPreviewContent] = useState('');

  const fetchSubscribers = useCallback(async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/newsletter/subscribers', {
        params: {
          status: statusFilter,
          search: searchTerm,
          limit: 100,
        },
      });

      if (response.data.success) {
        setSubscribers(response.data.data);
      }
    } catch (error) {
      message.error('Failed to fetch subscribers');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, searchTerm]);

  const fetchStats = useCallback(async () => {
    try {
      const [activeRes, unsubRes, bouncedRes] = await Promise.all([
        apiClient.get('/newsletter/subscribers/count', { params: { status: 'active' } }),
        apiClient.get('/newsletter/subscribers/count', { params: { status: 'unsubscribed' } }),
        apiClient.get('/newsletter/subscribers/count', { params: { status: 'bounced' } }),
      ]);

      setStats({
        activeCount: activeRes.data.count || 0,
        unsubscribedCount: unsubRes.data.count || 0,
        bouncedCount: bouncedRes.data.count || 0,
        totalCount: (activeRes.data.count || 0) + (unsubRes.data.count || 0) + (bouncedRes.data.count || 0),
      });
    } catch (error) {
      console.error('Failed to fetch stats', error);
    }
  }, []);

  useEffect(() => {
    void fetchSubscribers();
    void fetchStats();
  }, [fetchSubscribers, fetchStats]);

  const handleSendNewsletter = async (values: any) => {
    try {
      setSendLoading(true);
      const response = await apiClient.post('/newsletter/send', {
        subject: values.subject,
        htmlContent: values.htmlContent,
        textContent: values.textContent,
      });

      if (response.data.success) {
        message.success(response.data.message);
        setSendNewsletterModal(false);
        sendForm.resetFields();
      } else {
        // Show detailed error from backend
        const errorMsg = response.data.message || response.data.error || 'Failed to send newsletter';
        message.error(errorMsg);
        console.log('Newsletter send response:', response.data);
      }
    } catch (error: any) {
      // Show detailed error information
      let errorMsg = 'Failed to send newsletter';
      
      if (error.response) {
        // API returned an error response
        const status = error.response.status;
        const data = error.response.data;
        
        if (status === 401) {
          errorMsg = 'Unauthorized - Please log in again';
        } else if (status === 403) {
          errorMsg = `Access denied: ${data.message || 'Admin role required'}`;
        } else if (status === 400) {
          errorMsg = data.message || 'Invalid request - ' + (data.error || '');
        } else {
          errorMsg = data.message || data.error || `Server error (${status})`;
        }
        
        console.error('Newsletter API Error:', { status, data });
      } else if (error.message) {
        errorMsg = error.message;
        console.error('Newsletter error:', error);
      }
      
      message.error(errorMsg);
    } finally {
      setSendLoading(false);
    }
  };

  const columns = [
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      width: 250,
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      width: 150,
      render: (text: string) => text || '—',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status: string) => {
        const colorMap = {
          active: 'green',
          unsubscribed: 'red',
          bounced: 'orange',
        };
        return <Tag color={colorMap[status as keyof typeof colorMap]}>{status}</Tag>;
      },
    },
    {
      title: 'Subscribed',
      dataIndex: 'subscribed_at',
      key: 'subscribed_at',
      width: 150,
      render: (date: string) => date ? dayjs(date).format('MMM D, YYYY') : 'N/A',
    },
  ];

  return (
    <div style={{ padding: '20px' }}>
      <Tabs
        items={[
          {
            key: '1',
            label: 'Subscribers',
            children: (
              <>
                <div style={{ marginBottom: '20px', padding: '15px', background: '#f5f5f5', borderRadius: '4px' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '15px' }}>
                    <div>
                      <div style={{ fontSize: '12px', color: '#666' }}>Active Subscribers</div>
                      <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#52c41a' }}>{stats.activeCount}</div>
                    </div>
                    <div>
                      <div style={{ fontSize: '12px', color: '#666' }}>Unsubscribed</div>
                      <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#ff4d4f' }}>{stats.unsubscribedCount}</div>
                    </div>
                    <div>
                      <div style={{ fontSize: '12px', color: '#666' }}>Bounced</div>
                      <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#faad14' }}>{stats.bouncedCount}</div>
                    </div>
                    <div>
                      <div style={{ fontSize: '12px', color: '#666' }}>Total Subscribers</div>
                      <div style={{ fontSize: '24px', fontWeight: 'bold' }}>{stats.totalCount}</div>
                    </div>
                  </div>
                </div>

                <Space style={{ marginBottom: '15px', display: 'flex' }}>
                  <Input
                    placeholder="Search email or name..."
                    style={{ width: '300px' }}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                  <Select
                    placeholder="Filter by status"
                    allowClear
                    style={{ width: '150px' }}
                    value={statusFilter}
                    onChange={(value) => setStatusFilter(value)}
                    options={[
                      { label: 'Active', value: 'active' },
                      { label: 'Unsubscribed', value: 'unsubscribed' },
                      { label: 'Bounced', value: 'bounced' },
                    ]}
                  />
                  <Button
                    type="primary"
                    icon={<SendOutlined />}
                    onClick={() => setSendNewsletterModal(true)}
                  >
                    Send Newsletter
                  </Button>
                  <Button icon={<DownloadOutlined />}>Export CSV</Button>
                </Space>

                <Spin spinning={loading}>
                  <Table
                    columns={columns}
                    dataSource={subscribers.map((s) => ({ ...s, key: s.id }))}
                    pagination={{ pageSize: 20 }}
                    scroll={{ x: 800 }}
                  />
                </Spin>
              </>
            ),
          },
          {
            key: '2',
            label: 'Send Newsletter',
            children: (
              <div style={{ maxWidth: '800px', margin: '20px 0' }}>
                <Form
                  layout="vertical"
                  onFinish={handleSendNewsletter}
                  form={sendForm}
                  initialValues={{
                    subject: '',
                    htmlContent: '',
                    textContent: '',
                  }}
                >
                  <Form.Item
                    label="Subject"
                    name="subject"
                    rules={[{ required: true, message: 'Please enter newsletter subject' }]}
                  >
                    <Input placeholder="Newsletter subject" />
                  </Form.Item>

                  <Form.Item
                    label="HTML Content"
                    name="htmlContent"
                    rules={[{ required: true, message: 'Please enter newsletter HTML content' }]}
                  >
                    <Input.TextArea
                      rows={10}
                      placeholder="Enter HTML content for newsletter"
                    />
                  </Form.Item>

                  <Form.Item
                    label="Text Content (Optional)"
                    name="textContent"
                  >
                    <Input.TextArea
                      rows={5}
                      placeholder="Plain text version (will be auto-generated if not provided)"
                    />
                  </Form.Item>

                  <Space>
                    <Button
                      type="default"
                      onClick={() => {
                        const content = sendForm.getFieldValue('htmlContent');
                        setPreviewContent(content);
                        setPreviewDrawer(true);
                      }}
                    >
                      Preview
                    </Button>
                    <Button
                      type="primary"
                      icon={<SendOutlined />}
                      loading={sendLoading}
                      onClick={() => sendForm.submit()}
                      style={{ background: '#52c41a' }}
                    >
                      Send to {stats.activeCount} Active Subscribers
                    </Button>
                  </Space>
                </Form>
              </div>
            ),
          },
        ]}
      />

      {/* Send Newsletter Modal */}
      <Modal
        title="Send Newsletter"
        open={sendNewsletterModal}
        onCancel={() => setSendNewsletterModal(false)}
        footer={null}
        width={600}
      >
        <Form
          layout="vertical"
          onFinish={handleSendNewsletter}
          initialValues={{
            subject: '',
            htmlContent: '',
          }}
        >
          <Form.Item
            label="Subject"
            name="subject"
            rules={[{ required: true, message: 'Please enter newsletter subject' }]}
          >
            <Input placeholder="Newsletter subject" />
          </Form.Item>

          <Form.Item
            label="HTML Content"
            name="htmlContent"
            rules={[{ required: true, message: 'Please enter newsletter HTML content' }]}
          >
            <Input.TextArea
              rows={8}
              placeholder="Enter HTML content for newsletter"
            />
          </Form.Item>

          <Form.Item
            label="Text Content (Optional)"
            name="textContent"
          >
            <Input.TextArea
              rows={4}
              placeholder="Plain text version"
            />
          </Form.Item>

          <Button
            type="primary"
            icon={<SendOutlined />}
            loading={sendLoading}
            block
            htmlType="submit"
          >
            Send to {stats.activeCount} Active Subscribers
          </Button>
        </Form>
      </Modal>

      {/* Preview Drawer */}
      <Drawer
        title="Newsletter Preview"
        onClose={() => setPreviewDrawer(false)}
        open={previewDrawer}
      >
        <div
          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(previewContent || '') }}
          style={{ padding: '10px', border: '1px solid #ddd', borderRadius: '4px' }}
        />
      </Drawer>
    </div>
  );
};

export default NewsletterManagement;
